import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { School } from 'lucide-react';
import { useAuth } from '@/features/auth/AuthProvider';
import { getErrorMessage } from '@/shared/lib/api/errors';
import { Button } from '@/shared/ui';
import type { AcaoInterfaceMia } from './assistenteService';
import { abrirRegistro, mostrarNaTela } from './acoesTelaMia';
import { useAssistenteUi } from './assistenteUi';
import styles from './BlocosMia.module.css';

/**
 * Registro, no balao, do que a Mia fez na tela, com o atalho para repetir. A troca de escola
 * e a excecao: nao acontece sozinha, o cartao pede Confirmar (decisao de 22/10/2026).
 */
export function AcaoTelaMia({ acao }: { acao: AcaoInterfaceMia }) {
  const navigate = useNavigate();
  const { recolher } = useAssistenteUi();
  switch (acao.tipo) {
    case 'navegar':
      return (
        <p className={styles.abriu}>
          Abri <Link to={acao.path}>{acao.rotulo || 'a tela'}</Link>.
        </p>
      );
    case 'destacar':
      return (
        <p className={styles.abriu}>
          Destaquei {acao.rotulo || 'o elemento'} na tela.{' '}
          <button type="button" className={styles.linkBotao} onClick={() => mostrarNaTela(acao.alvo, recolher)}>
            Mostrar de novo
          </button>
        </p>
      );
    case 'abrir_registro':
      return (
        <p className={styles.abriu}>
          Abri o {acao.rotulo || 'registro'} para você revisar e postar.{' '}
          <button type="button" className={styles.linkBotao} onClick={() => abrirRegistro(acao, navigate)}>
            Abrir de novo
          </button>
        </p>
      );
    case 'trocar_escola':
      return <TrocaEscolaMia idEscola={acao.idEscola} nome={acao.rotulo} />;
    default:
      return null;
  }
}

function TrocaEscolaMia({ idEscola, nome }: { idEscola: number; nome: string }) {
  const { user, selecionarEscola } = useAuth();
  const [estado, setEstado] = useState<'pendente' | 'trocando' | 'cancelada' | 'erro'>('pendente');
  const [erro, setErro] = useState<string | null>(null);
  const jaAtiva = user?.idEscolaAtiva === idEscola;

  async function trocar() {
    setEstado('trocando');
    setErro(null);
    try {
      await selecionarEscola(idEscola);
    } catch (error) {
      setErro(getErrorMessage(error, 'Nao foi possivel trocar de escola.'));
      setEstado('erro');
    }
  }

  return (
    <section className={styles.trocaEscola} aria-label={`Trocar para ${nome}`}>
      <p className={styles.trocaTitulo}>
        <School size={16} aria-hidden /> Trocar para <strong>{nome}</strong>
      </p>
      {jaAtiva ? (
        <p className={styles.trocaNota}>Esta já é a escola ativa.</p>
      ) : estado === 'cancelada' ? (
        <p className={styles.trocaNota}>Troca cancelada.</p>
      ) : (
        <>
          <p className={styles.trocaNota}>Menu, permissões e dados passam a ser os dessa escola.</p>
          {erro ? (
            <p className={styles.trocaErro} role="alert">
              {erro}
            </p>
          ) : null}
          <div className={styles.acoes}>
            <Button size="sm" isLoading={estado === 'trocando'} onClick={() => void trocar()}>
              Confirmar
            </Button>
            <Button size="sm" variant="outline" disabled={estado === 'trocando'} onClick={() => setEstado('cancelada')}>
              Cancelar
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
